import { $inject } from '../utils/di-container.js';
import { FeatureService } from './feature.service.js';
import { makePredicate } from './badge-composition-rule.js';
import { planLevel } from './badge-plan-level.js';
import { LoggerService } from './logger.service.js';
import { flattenObject } from '../utils/object-utils.js';
import { UrlService } from './url.service.js';

/**
 * @typedef {object} _BadgeComposition_state
 * Holds badge state information (from user interaction)
 *
 * @property {boolean} maximized
 */

/**
 * @typedef {object} _BadgeComposition_input
 * Holds input / data like company and brand info
 *
 * @property {_Company} [company]
 * @property {_BrandPartner[]} [brands]
 * @property {_Product} [product]
 * @property {string} [certificateUrl]
 */

/**
 * @typedef {object} _BadgeComposition_aspect
 * Holds aspect-related properties
 *
 * @property {string} [flavor]
 *
 * @property {number} [baseZIndex]
 * @property {{top: number, left: number}} [nodeOffset]
 *
 * @property {'expanded'|'collapsed'} [type] - the actual aspect of the badge as it is displayed (different than state.maximized)
 * @property {'mid'|'bottom-right'} [fixedType]
 * @property {boolean} [hasSpaceAbove]
 * @property {object} [position]
 * @property {any} [position.settings] // old approach
 * @property {any} [position.settingsHorizontal]

 * @property {object} [position.vertical] // new approach
 * @property {any} [position.vertical.settings]
 * @property {object} [position.horizontal]
 * @property {any} [position.horizontal.settings]
 *
 * @property {object} [position.specs]
 * @property {object} [position.specs.vertical]
 * @property {'top'|'bottom'} [position.specs.vertical.reference]
 * @property {number} [position.specs.vertical.distance]
 * @property {'%'|'px'} [position.specs.vertical.unit]
 * @property {boolean} [position.specs.vertical.realign]
 * @property {object} [position.specs.horizontal]
 * @property {'left'|'right'} [position.specs.horizontal.reference]
 * @property {number} [position.specs.horizontal.distance]
 * @property {'%'|'px'} [position.specs.horizontal.unit]
 * @property {boolean} [position.specs.horizontal.realign]
 *
 * @property {Object<string, string|null>} [style]
 */

/**
 * @typedef {object} _BadgeComposition_constraints
 * Holds the constraints applied to the badge
 *
 * @property {boolean} [canMaximize]
 * @property {boolean} [alwaysMaximized]
 * @property {boolean} [hideContainerWhenMinimized]
 */

/**
 * @typedef {object} _BadgeComposition_container
 * Holds information regarding the badge container / outline
 *
 * @property {boolean} [show]
 * @property {'round-rectangle'|'round-rectangle-left'|'round-rectangle-concealed-top-left'|'pill'|'pill-left'|'bottom'} [type]
 */

/**
 * @typedef {object} _BadgeComposition_parts
 * Contains detailed information about visual parts of the badge
 *
 * @property {object} [accordion]
 * @property {boolean} [accordion.show]
 * @property {'md'|'sm'} [accordion.size]
 * @property {boolean} [accordion.useOverlay]
 *
 * @property {object} [carousel]
 * @property {boolean} [carousel.show]
 * @property {'header'|'contents'|null} [carousel.location]
 * @property {'xs'|'md'|'lg'} [carousel.size]
 * @property {boolean} [carousel.includeBottomText]
 *
 * @property {object} [menu]
 * @property {boolean} [menu.show]
 * @property {'external'|'internal'} [menu.placement]
 * @property {number} [menu.extraOffsetRight]
 * @property {boolean} [menu.includeMinimize]
 * @property {'minimize'|'deactivate'} [menu.minimizeAction]
 * @property {number} [menu.zIndex]
 *
 * @property {object} [contents]
 * @property {boolean} [contents.present]
 * @property {boolean} [contents.show]
 * @property {object} [contents.texts]
 * @property {symbol} [contents.texts.originalityConfirmedTitle]
 * @property {symbol} [contents.texts.originalityConfirmedText]
 * @property {symbol} [contents.texts.authRetailerTitle]
 * @property {symbol} [contents.texts.authRetailerText]
 * @property {symbol} [contents.texts.shopCheckTitle]
 * @property {symbol} [contents.texts.shopCheckText]
 *
 * @property {object} [sealAsButton]
 * @property {boolean} [sealAsButton.show]
 * @property {'normal'|'close'} [sealAsButton.display]
 * @property {Object<string, string>} [sealAsButton.style]
 *
 * @property {object} [shadowZone]
 * @property {boolean} [shadowZone.show]
 * @property {string} [shadowZone.width]
 * @property {string} [shadowZone.height]
 *
 * @property {object} [maximizeArrow]
 * @property {boolean} [maximizeArrow.show]
 *
 * @property {object} [headerText]
 * @property {boolean} [headerText.show]
 * @property {'minimal'|'full'|'brand-link'} [headerText.type]
 * @property {'only-authby'|'only-brandCount'} [headerText.minimalOption]
 * @property {boolean} [headerText.center]
 *
 * @property {object} [headerImage]
 * @property {boolean} [headerImage.show]
 *
 * @property {object} [aubySeal]
 * @property {boolean} [aubySeal.show]
 * @property {boolean} [aubySeal.clickable]
 * @property {'xs'|'md'} [aubySeal.offsetType]
 * @property {boolean} [aubySeal.offsetBottom]
 * @property {string} [aubySeal.imageSrc]
 *
 * @property {object} [tuvSeal]
 * @property {boolean} [tuvSeal.show]
 * @property {string} [tuvSeal.imageSrc]
 * @property {boolean} [tuvSeal.showInAccordion]
 *
 * @property {object} [dateBanner]
 * @property {boolean} [dateBanner.show]
 * @property {'md'|'lg'} [dateBanner.size]
 *
 * @property {object} [button]
 * @property {boolean} [button.show]
 * @property {'lg'|'md'|'sm'} [button.size]
 *
 * @property {object} [tooltips]
 * @property {boolean} [tooltips.show]
 * @property {number} [tooltips.zIndex]
 */

/**
 * @typedef {object} _BadgeCompositionRaw
 * @property {_BadgeComposition_input} [input]
 * @property {_BadgeComposition_state} [state]
 * @property {_BadgeComposition_aspect} [aspect]
 * @property {_BadgeComposition_constraints} [constraints]
 * @property {_BadgeComposition_container} [container]
 * @property {_BadgeComposition_parts} [parts]
 * @property {object} [flags]
 */

/**
 * A structured object containing pre-calculated information that defines the way a @{link BadgeComponent} will be
 * rendered, which can be used to render various parts of the badge.
 * The composition is based on a configuration (input) and based on some rules it defines badge attributes on itself
 * for easy access (output).
 */
export class BadgeComposition {

  /** @type {FeatureService} */
  _featureService;

  /** @type {UrlService} */
  _urlService;

  /** @type {LoggerService} */
  _logger;

  /** @type {string} */
  _debug;

  /** @type {string} */
  code;

  /** @type {_BadgeType} */
  badgeType;

  /** @type {_BadgeComposition_input} */
  input;

  /** @type {_BadgeComposition_state} */
  state;

  /** @type {_BadgeComposition_aspect} */
  aspect;

  /** @type {_BadgeComposition_constraints} */
  constraints;

  /** @type {_BadgeComposition_container} */
  container;

  /** @type {_BadgeComposition_parts} */
  parts;

  /** @type {object} */
  flags = {};

  /**
   * Helpers for creating {@link BadgeCompositionRule}s using a DSL-like approach
   */
  checkers = {
    check: fn => makePredicate(() => fn()),
    featureIsEnabled: value => makePredicate(() => this._featureService.isEnabled(value)),
    platformIs: value => makePredicate(() => this.badgeType.platform === value),
    variantIs: value => makePredicate(() => this.badgeType.variant === value),
    planIs: value => makePredicate(() => this.badgeType.plan === value),
    planIsAtLeast: value => makePredicate(() => planLevel[this.badgeType.plan] >= planLevel[value]),
    isTuvVerified: makePredicate(() => !!this.input.company.tuvApprovalDate),
    originalPlanIs: value => makePredicate(() => (this.badgeType.originalPlan && this.badgeType.originalPlan === value) || (!this.badgeType.originalPlan && this.badgeType.plan === value)),
    sandboxEnvironment: makePredicate(() => this.badgeType.environment === 'test'),
    stateIsMaximized: makePredicate(() => this.state.maximized),
    aspectTypeIs: value => makePredicate(() => this.aspect.type === value),
    hasProduct: makePredicate(() => !!this.input.product),
    hasProductWithId: makePredicate(() => this.input.product && this.input.product.id),
    minAspectOffsetTop: value => makePredicate(() => this.aspect.nodeOffset && this.aspect.nodeOffset.top > value),
    positionLocationVIs: value => makePredicate(() => this.aspect.position.vertical.location === value),
    positionLocationHIs: value => makePredicate(() => this.aspect.position.horizontal.location === value),
    isT0: makePredicate(() => !!this.badgeType.t0),
    isD2c: makePredicate(() => this.input.product && this.input.product.officialBrandShop),
    isBS: makePredicate(() => this.input.product && this.input.product.brandSponsored)
  };

  constructor(config) {
    const {
      code,
      debug,
      type,
      position,
      company,
      brands,
      product,
      badgeNodeOffset,
      maximized,
      flavor
    } = config;

    this.code = code;
    this._debug = debug;

    this.badgeType = type;

    this.input = {
      company,
      brands,
      product
    };

    this.state = {
      maximized
    };

    this.aspect = {
      flavor,
      position,
      nodeOffset: badgeNodeOffset,
      baseZIndex: type.options.zIndex
    };
  }

  [$inject](obtain) {
    this._featureService = obtain(FeatureService);
    this._urlService = obtain(UrlService);
    this._logger = obtain(LoggerService);
  }

  getRaw() {
    return {
      code: this.code,
      badgeType: this.badgeType,
      input: this.input,
      state: this.state,
      aspect: this.aspect,
      constraints: this.constraints,
      parts: this.parts,
      flags: this.flags
    };
  }

  /**
   * @param {BadgeCompositionRule[]} rules
   * @returns {BadgeComposition}
   */
  execute(rules) {
    if (this._debug) {
      this._executeDebug(rules);
    } else {
      rules.forEach(rule => rule.applyOn(this));
    }
    return this;
  }

  _executeDebug(rules) {
    const debugExec = this._debug.indexOf('composition.execution') >= 0;
    const debugOutcome = this._debug.indexOf('composition.outcome') >= 0;

    // detailed account of rules passed and how it affects composition data
    const ruleTrace = {};
    const activeRules = [];

    rules.forEach(rule => {
      const resultPart = rule.applyOn(this);
      if (debugExec) {
        if (resultPart) {
          activeRules.push(rule.code);
          const flatRawComposition = flattenObject(resultPart);
          Object.entries(flatRawComposition).forEach(([key, value]) => {
            if (!ruleTrace[key]) {
              ruleTrace[key] = [];
            }
            ruleTrace[key].push({ rule: rule.code, value });
          });
        }
      }
    });

    if (debugOutcome) {
      this._logger.debug('debug/composition/outcome', `[${this.code}] Composition outcome:\n${JSON.stringify(this.getRaw(), null, 2)}`, () => ({ badgeType: this.badgeType, outcome: this.getRaw() }));
    }

    if (debugExec) {
      this._logger.debug('debug/composition/rules', `[${this.code}] Composition active rules:\n${activeRules.map(rule => '  ' + rule).join('\n')}`, () => ({ badgeType: this.badgeType, rules: activeRules }));

      const traces = Object.entries(ruleTrace)
        .map(([key, traces]) => `  ${key}:\n${traces.map(({ rule, value }) => `    rule(${rule}) => ${value ? value.toString() : value}`).join('\n')}`)
        .join('\n');
      this._logger.debug('debug/composition/execution', `[${this.code}] Composition execution:\n${traces}`, () => ({ badgeType: this.badgeType, traces }));
    }
  }

}
